import { useEffect, useRef } from "react";

/**
 * How far an element has travelled through the viewport, as 0 → 1.
 *
 * Same shape as useParallax and for the same reason: one scroll listener and
 * one animation frame for the whole page, however many sections are asking.
 * Several sections of the about page track scroll at once — a listener each is
 * how a page like this starts dropping frames on a trackpad.
 *
 * The callback is handed the progress and is expected to write styles itself,
 * never to setState. A scroll-linked effect that re-renders React on every
 * frame is the other way this gets slow.
 */

const targets = new Set();
let frame = 0;
let listening = false;

function update() {
  frame = 0;
  const viewport = window.innerHeight;

  targets.forEach((entry) => {
    const node = entry.node;
    if (!node) return;

    const rect = node.getBoundingClientRect();

    // Off screen by more than a viewport in either direction: nothing to
    // report, and reading is cheap compared with writing.
    if (rect.bottom < -viewport || rect.top > viewport * 2) {
      // Still clamp once on the way out, so a section scrolled past quickly
      // is left finished rather than frozen half way.
      if (entry.last !== null && entry.last > 0 && entry.last < 1) {
        const settled = rect.bottom < 0 ? 1 : 0;
        entry.last = settled;
        entry.onProgress(settled, rect);
      }
      return;
    }

    // progress = 0 when the element's top sits at `from` of the viewport,
    // 1 when its bottom sits at `to`. Expressed against rect.top so both
    // ends are the same measurement.
    const startTop = entry.from * viewport;
    const endTop = entry.to * viewport - rect.height;
    const span = endTop - startTop;

    const raw = span === 0 ? 1 : (rect.top - startTop) / span;
    const progress = raw < 0 ? 0 : raw > 1 ? 1 : raw;

    // Skip the write when nothing meaningful moved. Sub-pixel churn on a
    // custom property still invalidates style for the whole subtree.
    if (entry.last !== null && Math.abs(progress - entry.last) < 0.0015) return;

    entry.last = progress;
    entry.onProgress(progress, rect);
  });
}

function schedule() {
  if (!frame) frame = requestAnimationFrame(update);
}

/**
 * @param onProgress called with (progress, rect) on every frame the element
 *   moves. Must not setState — write to the DOM.
 * @param from viewport fraction the element's top is at when progress is 0.
 * @param to   viewport fraction the element's bottom is at when progress is 1.
 * @param disabled skip tracking entirely (used to drop scroll-driven layout on
 *   small screens, where these sections fall back to ordinary stacking).
 * @param ref optional caller-owned ref, so a component that needs to touch the
 *   node from its own callback can declare it before the callback exists.
 */
function useScrollProgress({
  onProgress,
  from = 1,
  to = 0,
  disabled = false,
  ref: externalRef
}) {
  const ownRef = useRef(null);
  const ref = externalRef || ownRef;

  // The callback is nearly always an inline arrow, so subscribing to it
  // directly would tear the entry down and rebuild it on every render.
  const handler = useRef(onProgress);
  handler.current = onProgress;

  useEffect(() => {
    const node = ref.current;
    if (!node || disabled) return;

    const entry = {
      node,
      from,
      to,
      last: null,
      onProgress: (progress, rect) => handler.current(progress, rect)
    };

    targets.add(entry);

    if (!listening) {
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule, { passive: true });
      listening = true;
    }

    schedule();

    return () => {
      targets.delete(entry);

      if (targets.size === 0 && listening) {
        window.removeEventListener("scroll", schedule);
        window.removeEventListener("resize", schedule);
        listening = false;
        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }
      }
    };
    // `ref` is either the caller's or this hook's own — a ref object in both
    // cases, so its identity is stable and listing it costs nothing.
  }, [from, to, disabled, ref]);

  return ref;
}

export default useScrollProgress;
